import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { submitBusinessRegistration } from "../../services/apiService";
import { INDUSTRIES } from "../../data/categories";

const NAVY = "#2a2420";
const ORANGE = "#607840";

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" style={{ color: NAVY }}>
        {label}
        {required && <span style={{ color: ORANGE }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const baseInput = "w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all";

function inputStyle() {
  return { border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" } as React.CSSProperties;
}

function focusInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.currentTarget.style.border = `1.5px solid ${ORANGE}`;
}
function blurInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.currentTarget.style.border = `1.5px solid ${NAVY}22`;
}


export default function CreateBusinessPage() {
  const navigate = useNavigate();

  const [name, setName]         = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [address, setAddress]   = useState("");
  const [industry, setIndustry] = useState("");
  const [abn, setAbn]           = useState("");
  const [phone, setPhone]       = useState("");
  const [email, setEmail]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleBack() {
    if (typeof window !== "undefined" && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/", { replace: true });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await submitBusinessRegistration({
        name:              name.trim(),
        contactPersonName: contactPersonName.trim(),
        address:           address.trim(),
        industry:          industry,
        abn:               abn.trim(),
        phone:             phone.trim(),
        email:             email.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#fefcf8" }}>
        <div className="w-full max-w-md text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: `${ORANGE}12` }}
          >
            <CheckCircle size={32} style={{ color: ORANGE }} />
          </div>
          <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
            Registration submitted
          </h1>
          <p className="text-sm leading-relaxed mb-1" style={{ color: NAVY, opacity: 0.6 }}>
            Your request to register <strong style={{ opacity: 1 }}>{name}</strong> has been received.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: NAVY, opacity: 0.6 }}>
            An admin will review it shortly. You'll receive a notification once it's processed, and the business will appear on your businesses page automatically if approved.
          </p>
          <button
            onClick={() => navigate("/businesses")}
            className="mt-8 px-6 py-2.5 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
          >
            Back to businesses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#fefcf8" }}>
      <div className="w-full max-w-md">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm mb-6"
          style={{ color: NAVY, opacity: 0.5 }}
        >
          <ArrowLeft size={14} />
          Back
        </button>

        <div
          className="rounded-2xl p-8"
          style={{ backgroundColor: "#fefcf8", border: `1.5px solid ${NAVY}1a`, boxShadow: "0 4px 24px rgba(80,55,30,0.08)" }}
        >
          <h1 className="text-xl font-extrabold mb-1" style={{ color: NAVY }}>
            Register a business
          </h1>
          <p className="text-sm mb-6" style={{ color: NAVY, opacity: 0.5 }}>
            Bill Lens is currently in development testing. Our team will review your request and stay in sync with you throughout setup and testing.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Business name" required>
              <input
                type="text" required value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. The Corner Café"
                className={baseInput} style={inputStyle()}
                onFocus={focusInput} onBlur={blurInput}
              />
            </Field>

            <Field label="Contact person name" required>
              <input
                type="text" required value={contactPersonName}
                onChange={(e) => setContactPersonName(e.target.value)}
                placeholder="e.g. Jamie Nguyen"
                className={baseInput} style={inputStyle()}
                onFocus={focusInput} onBlur={blurInput}
              />
            </Field>

            <Field label="Industry" required>
              <select
                required value={industry} onChange={(e) => setIndustry(e.target.value)}
                className={`${baseInput} appearance-none`} style={inputStyle()}
                onFocus={focusInput} onBlur={blurInput}
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>

            <Field label="ABN" required>
              <input
                type="text" required value={abn} onChange={(e) => setAbn(e.target.value)}
                placeholder="12 345 678 901"
                className={baseInput} style={inputStyle()}
                onFocus={focusInput} onBlur={blurInput}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" required>
                <input
                  type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+61 400 000 000"
                  className={baseInput} style={inputStyle()}
                  onFocus={focusInput} onBlur={blurInput}
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="hello@business.com"
                  className={baseInput} style={inputStyle()}
                  onFocus={focusInput} onBlur={blurInput}
                />
              </Field>
            </div>

            <Field label="Address" required>
              <input
                type="text" required value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Sydney NSW 2000"
                className={baseInput} style={inputStyle()}
                onFocus={focusInput} onBlur={blurInput}
              />
            </Field>

            {error && (
              <p className="text-xs px-3 py-2 rounded-lg"
                style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Submitting…" : "Submit registration"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
